import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import type { ReactNode } from 'react';
import { Api, ApiError, serverClock, TokenStore } from '@limuzin/kit';
import type { MeView, PassengerRideView } from '@limuzin/passenger';
import {
  askSupport, endSession, getAvatar, getConfig, getMe, getMyRides, getSupportThreads, rateRide,
  replyToSupport, sendChatMedia, sendConsent, sendMessage, uploadSupportImage,
} from './api';
import { clearRatings, flushRatings, type HeldRating } from './ratings';
import { clearConsent, flushConsent } from './consent';
import { NEW, deliverWith } from './outbox';
import { FINISHED_RIDE_WINDOW, readClosedRides, writeClosedRide } from './closed';
import {
  RideNews, cancelledTag, chatRoute, chatTag, followTaps, notify, onGranted, preview, refusedTag, threadRoute,
  threadTag, withdraw, withdrawArrived,
} from './notify';
import { enrol, leave } from './push';
import { alertsOn, onAlertsChanged } from './alerts';
import { Realtime, type ChannelState } from './realtime';
import { ACCEPTED, RideSounds, SET_OFF, play, primeVoice } from './sounds';
import { toast } from './widgets';
import { appVersion } from './build';

/**
 * Who is signed in, what rides they have, and the socket that keeps both current.
 *
 * The Flutter app spreads this across a handful of Riverpod providers; here it is one
 * context, because there is one consumer of each piece and a second mechanism to keep
 * correct would buy nothing.
 */

interface Session {
  api: Api;
  me: MeView | null;
  rides: PassengerRideView[];
  /** True until a stored token has been confirmed against the server. */
  restoring: boolean;
  /**
   * The stored session could not be checked because the server did not answer. It
   * is not an answer about the session, so nobody is signed out for it: the screen
   * says so, and offers to ask again.
   */
  unreachable: boolean;
  /** Asks the server about the stored session again. */
  retry: () => void;

  /** True while the very first ride list is on its way. */
  loading: boolean;
  /**
   * Whether a ride list has arrived since signing in. Until one has, an empty `rides`
   * says nothing — a car may well be on its way — and the ride tab says it does not
   * know rather than offering «Новый заказ».
   */
  ridesKnown: boolean;
  /** Whether the last attempt at the ride list failed, with none known yet. */
  ridesFailed: boolean;
  /** How the live channel stands; `down` means what is on screen may be behind. */
  connection: ChannelState;
  reload: () => Promise<void>;
  adoptRide: (ride: PassengerRideView) => void;
  refreshMe: () => Promise<void>;
  setMe: (me: MeView) => void;
  signedIn: (me: MeView) => void;
  signOut: () => void;
  /**
   * Bumped whenever «Поддержка» answers something. The inbox and the thread on
   * screen watch it and refetch: one number is cheaper than a second store for
   * conversations that are opened a few times a month.
   */
  supportSignal: number;
  /**
   * Answers from «Поддержка» not opened yet, across every thread — the dot on
   * «Профиль» and on its «Поддержка» row. A closed tab hears nothing, so it is
   * asked again every start and whenever an answer or a read comes past.
   */
  supportUnread: number;
  refreshSupport: () => void;
  /**
   * Bumped whenever the driver writes in a ride's chat. The chat on screen watches it
   * and refetches, rather than waiting for its own poll.
   */
  chatSignal: number;
  /**
   * The conversation on screen — `chat:<ride>` or `support:<thread>` — or null. Said
   * by the sheet that shows it, so nothing is notified about what is being read.
   */
  reading: (tag: string | null) => void;
  /**
   * Rides the passenger has read and closed — see `useActiveRide`. Kept on the
   * device rather than only in this tab, so a reload does not hand back a summary
   * that has already been dealt with; see `./closed`.
   */
  dismissed: Set<string>;
  dismiss: (rideId: string) => void;
}

const Ctx = createContext<Session | null>(null);

/**
 * Same origin in development, where Vite proxies `/api`. `VITE_API_BASE` is for a
 * deployment that serves the bundle from somewhere other than the API.
 */
const BASE = import.meta.env.VITE_API_BASE ?? '';

/**
 * `customer`, not `admin`. The kit's store namespaces the key precisely so that an
 * operator signed into the panel on the same machine does not overwrite — or read —
 * a passenger's session.
 */
const store = new TokenStore('customer');
// `web/1.1.2` on every request: what the panel shows as this passenger's app.
const api = new Api(BASE, store, `web/${appVersion}`);
const realtime = new Realtime();
const rideSounds = new RideSounds();

/** How often «Нет связи с сервером» asks the server again by itself. */
const UNREACHABLE_RETRY_MS = 15_000;

/** How a rating left at «Готово» is sent, now or once there is a connection. */
export const deliverRating = (rating: HeldRating) =>
  rateRide(api, rating.rideId, rating.stars, rating.comment, rating.at);
const rideNews = new RideNews();

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeView | null>(null);
  const [rides, setRides] = useState<PassengerRideView[]>([]);
  const [restoring, setRestoring] = useState(api.isSignedIn);
  const [unreachable, setUnreachable] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(false);
  const [ridesKnown, setRidesKnown] = useState(false);
  const [ridesFailed, setRidesFailed] = useState(false);
  const [connection, setConnection] = useState<ChannelState>(realtime.state);
  // Read before the first render rather than in an effect after it: a summary the
  // passenger has dealt with must not be on screen at all, not even for the paint it
  // would take to read the store and withdraw it.
  const [dismissed, setDismissed] = useState<Set<string>>(readClosedRides);
  const [supportSignal, setSupportSignal] = useState(0);
  const [supportUnread, setSupportUnread] = useState(0);
  // Asked for by a thread just read, so the dot goes out — apart from
  // `supportSignal`, which the thread on screen itself reloads on.
  const [unreadAsked, setUnreadAsked] = useState(0);
  const [chatSignal, setChatSignal] = useState(0);
  // Read from the socket's callbacks, which fire outside React's rendering.
  const readingRef = useRef<string | null>(null);
  // The Web Push endpoint the server knows this browser by, or null (ADR 0027).
  const endpointRef = useRef<string | null>(null);

  // Held in a ref as well as in state: the socket callback and the interval below
  // both fire outside React's rendering, and neither should re-subscribe to get a
  // current value.
  const signedInRef = useRef(false);
  signedInRef.current = me !== null;

  const reload = useCallback(async () => {
    if (!api.isSignedIn) return;
    try {
      const list = await getMyRides(api);
      setRides(list);
      setRidesKnown(true);
      setRidesFailed(false);
      withdrawArrived(new Set(list.filter((r) => COMING.has(r.status)).map((r) => r.id)));
      // A ride taken, a car set off, a car at the pickup: heard in whatever the
      // passenger chose for it (ADR 0031), in a tab in front of them or behind.
      for (const event of rideSounds.saw(list)) {
        // «Заказ принят» and «Водитель выехал к вам» are the ones a push says as
        // well, in the system's sound. Said by both, they would be heard twice.
        const pushed = event === ACCEPTED || event === SET_OFF;
        if (pushed && pushSaysIt(endpointRef.current)) continue;
        play(event);
      }
      // A ride taken, or given up on, while nobody was looking at this tab (ADR
      // 0032). A tab in front of the passenger is already saying it, and a hidden
      // one of a subscribed browser leaves it to the push.
      const news = rideNews.saw(list);
      if (document.visibilityState !== 'visible' && !pushSaysIt(endpointRef.current)) {
        news.forEach(notify);
      }
    } catch {
      // A failed refresh must not blank a list that is on screen and correct. The
      // passenger is looking at a live ride; last known is better than nothing. One
      // that never arrived is said as such — see `ridesKnown`.
      setRidesFailed(true);
    }
  }, []);

  const signOut = useCallback(() => {
    // Best effort: the refresh token is revoked on the server where it can be, and
    // the session here goes either way. A passenger who signs out ends up signed out
    // even when the network does not answer.
    const tokens = store.read();
    if (tokens) void endSession(api, tokens.refreshToken).catch(() => {});
    realtime.disconnect();
    leave();
    setUnreachable(false);
    endpointRef.current = null;
    realtime.present({ endpoint: null });
    api.forget();
    clearRatings();
    clearConsent();
    rideSounds.reset();
    rideNews.reset();
    setMe(null);
    setRides([]);
    setRidesKnown(false);
    setRidesFailed(false);
    setDismissed(new Set());
  }, []);

  useEffect(() => {
    api.onSignedOut = () => {
      realtime.disconnect();
      leave();
      endpointRef.current = null;
      rideSounds.reset();
      rideNews.reset();
      setMe(null);
      setRides([]);
    };
  }, []);

  // Safari plays a sound only after a press; the first one anywhere opens the way
  // for «Машина на месте» to be heard with the phone lying on the table.
  useEffect(() => primeVoice(), []);

  // A stored token is a claim, not a session — it may have been revoked. Ask before
  // drawing anything. A server that did not answer has said nothing about it, and
  // the session stays on the device for «Повторить».
  useEffect(() => {
    if (!api.isSignedIn) return;
    let live = true;
    setLoading(true);
    setRestoring(true);
    setUnreachable(false);
    getMe(api)
      .then(async (who) => {
        if (!live) return;
        setMe(who);
        await reload();
      })
      .catch((problem: unknown) => {
        if (!live) return;
        if (problem instanceof ApiError && problem.code === 'offline') setUnreachable(true);
        else api.forget();
      })
      .finally(() => {
        if (!live) return;
        setRestoring(false);
        setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [reload, attempt]);

  // «Нет связи с сервером» asks again by itself — every quarter of a minute, when the
  // browser says it is back online, and when the tab comes back into view — so that a
  // passenger whose signal returns is not left looking at it until they think to tap.
  // Quietly: the screen stays until the server answers, rather than blinking through
  // the splash each time it does not.
  useEffect(() => {
    if (!unreachable) return;
    let asking = false;
    const ask = () => {
      if (asking || document.visibilityState !== 'visible') return;
      asking = true;
      getMe(api)
        .then(() => setAttempt((n) => n + 1))
        .catch(() => {})
        .finally(() => {
          asking = false;
        });
    };
    const timer = window.setInterval(ask, UNREACHABLE_RETRY_MS);
    window.addEventListener('online', ask);
    document.addEventListener('visibilitychange', ask);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('online', ask);
      document.removeEventListener('visibilitychange', ask);
    };
  }, [unreachable]);

  // The live channel, for as long as somebody is signed in.
  useEffect(() => {
    if (!me || !api.isSignedIn) return;
    realtime.onRide = () => void reload();
    // The ride list carries each chat's unread count — the dot — so a message, and a
    // chat read on another screen, both ask for it again.
    realtime.onMessage = (message) => {
      void reload();
      setChatSignal((n) => n + 1);
      const tag = chatTag(message.rideId);
      if (isReading(readingRef.current, tag) || pushSaysIt(endpointRef.current)) return;
      notify({
        title: message.senderName ?? (message.number ? `Заказ № ${message.number}` : 'Водитель'),
        body: preview(message),
        tag,
        go: chatRoute(message.rideId),
      });
    };
    // The driver cancelled a ride. Its `ride.updated` takes it off the screen, and a
    // car that vanishes with «Новый заказ» back where it was reads as the app losing
    // it — so this says why: along the bottom in a tab somebody is looking at, and in
    // a notification otherwise, in the words `cancelledNote` pushes on the server.
    realtime.onCancelled = (cancel) => {
      const said = `Водитель отменил заказ № ${cancel.number}`;
      if (document.visibilityState === 'visible') {
        toast(said);
        return;
      }
      if (pushSaysIt(endpointRef.current)) return;
      notify({
        title: said,
        body: [cancel.from, cancel.to].filter(Boolean).join(' → '),
        tag: cancelledTag(cancel.rideId),
        go: '#/',
      });
    };
    // The driver of a предзаказ gave it up, and it is looking for another. The card in
    // «Запланированные» says so from now on; this says it as it happens — the
    // passenger was told somebody was booked, and is not watching that card the day
    // before — in the words `refusedNote` pushes on the server.
    realtime.onRefused = (refusal) => {
      const said = 'Водитель отказался — ищем другого';
      if (document.visibilityState === 'visible') {
        toast(said);
        return;
      }
      if (pushSaysIt(endpointRef.current)) return;
      notify({
        title: said,
        body: [refusal.from, refusal.to].filter(Boolean).join(' → '),
        tag: refusedTag(refusal.rideId),
        go: '#/orders',
      });
    };
    realtime.onRead = () => void reload();
    // Back after a drop: what the server said meanwhile was said to nobody, so the
    // rides are asked for now rather than at the next poll, and the ratings left
    // without a connection go (ADR 0030).
    realtime.onState = (state) => {
      setConnection(state);
      if (state !== 'live') return;
      void reload();
      void flushRatings(me.id, deliverRating);
      void flushConsent(me.id, (consent) => sendConsent(api, consent));
    };
    // The driver deleted something for both sides: the chat on screen asks again, and
    // the server no longer has it to give.
    realtime.onDeleted = () => {
      void reload();
      setChatSignal((n) => n + 1);
    };
    realtime.onSupportDeleted = () => setSupportSignal((n) => n + 1);
    realtime.onSupport = (answer) => {
      setSupportSignal((n) => n + 1);
      const tag = threadTag(answer.threadId);
      if (isReading(readingRef.current, tag) || pushSaysIt(endpointRef.current)) return;
      notify({
        title: answer.number ? `Поддержка · Обращение № ${answer.number}` : 'Поддержка',
        body: answer.text ?? 'Новый ответ',
        tag,
        go: threadRoute(answer.threadId),
      });
    };
    // Whether anybody is looking at this tab, which decides whether the server
    // pushes to this browser as well (ADR 0027).
    const shown = () => realtime.present({ visible: document.visibilityState === 'visible' });
    shown();
    document.addEventListener('visibilitychange', shown);
    realtime.connect((rejected) => api.accessToken(rejected));
    return () => {
      document.removeEventListener('visibilitychange', shown);
      realtime.disconnect();
    };
  }, [me, reload]);

  // This browser, for Web Push (ADR 0027): at every start, and the moment the
  // passenger allows notifications. Keyed on who, not on the profile, which is
  // replaced by every edit to it.
  const who = me?.id ?? null;
  const town = me?.passenger?.townId ?? null;
  useEffect(() => {
    if (who) void getConfig(api, town).catch(() => {});
  }, [who, town]);

  useEffect(() => {
    if (!who) return;
    let live = true;
    const subscribe = () => {
      // «Уведомления о поездке» off: this browser is left unreachable.
      if (!alertsOn()) {
        leave();
        endpointRef.current = null;
        realtime.present({ endpoint: null });
        return;
      }
      void enrol(api).then((endpoint) => {
        if (!live) return;
        endpointRef.current = endpoint;
        realtime.present({ endpoint });
      });
    };
    subscribe();
    const stop = onGranted(subscribe);
    const turned = onAlertsChanged(subscribe);
    return () => {
      live = false;
      stop();
      turned();
    };
  }, [who]);

  useEffect(() => {
    if (!who) return;
    let live = true;
    getSupportThreads(api)
      .then((threads) => {
        if (live) setSupportUnread(threads.reduce((sum, thread) => sum + thread.unread, 0));
      })
      .catch(() => {
        // The dot stays as it was; it is asked again on the next answer.
      });
    return () => {
      live = false;
    };
  }, [who, supportSignal, unreadAsked]);

  // What this side says in a conversation goes out through the outbox, which needs
  // the session to say it with — and nothing of one passenger's is left in it for the
  // next.
  useEffect(() => {
    if (!who) return;
    deliverWith(async (message) => {
      if (message.kind === 'ride') {
        const [file] = message.files;
        if (file) await sendChatMedia(api, message.conversation, message.text, file);
        else await sendMessage(api, message.conversation, message.text);
        return null;
      }
      const keys: string[] = [];
      for (const file of message.files) keys.push((await uploadSupportImage(api, file)).storageKey);
      if (message.conversation === NEW) return (await askSupport(api, message.text, keys)).id;
      await replyToSupport(api, message.conversation, message.text, keys);
      return null;
    });
    return () => deliverWith(null);
  }, [who]);

  // A notification tapped comes back from the service worker naming where to go.
  useEffect(() => followTaps(), []);

  // Stable, because the sheets that say it do so from effects.
  const reading = useCallback((tag: string | null) => {
    readingRef.current = tag;
    // Opened: what was said about it is no longer news.
    if (tag) withdraw(tag);
  }, []);

  /**
   * A poll behind the socket, not instead of it.
   *
   * Two things the socket cannot do: a phone that has been asleep in a pocket comes
   * back with a dead connection and no missed events, and a предзаказ opening at
   * 07:00 is a server-side clock nobody emits to a passenger. Thirty seconds is often
   * enough for both and rare enough not to matter on a rural connection — and it only
   * runs while the tab is actually being looked at.
   */
  useEffect(() => {
    if (!me) return;
    const tick = () => {
      if (document.visibilityState === 'visible') void reload();
    };
    const timer = window.setInterval(tick, 30_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [me, reload]);

  const value = useMemo<Session>(
    () => ({
      api,
      me,
      rides,
      restoring,
      unreachable,
      retry: () => setAttempt((n) => n + 1),
      loading,
      ridesKnown,
      ridesFailed,
      connection,
      reload,
      adoptRide: (ride) => {
        setRides((all) => [ride, ...all.filter((r) => r.id !== ride.id)]);
        setRidesKnown(true);
      },
      refreshMe: async () => setMe(await getMe(api)),
      setMe,
      signedIn: (who) => {
        setMe(who);
        void reload();
      },
      signOut,
      supportSignal,
      supportUnread,
      refreshSupport: () => setUnreadAsked((n) => n + 1),
      chatSignal,
      reading,
      dismissed,
      dismiss: (rideId) => {
        // Outside the updater, which React is free to run more than once.
        writeClosedRide(rideId);
        setDismissed((all) => (all.has(rideId) ? all : new Set(all).add(rideId)));
      },
    }),
    [
      me, rides, restoring, unreachable, loading, ridesKnown, ridesFailed, connection, reload,
      signOut,
      supportSignal, supportUnread, chatSignal, reading, dismissed,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const value = useContext(Ctx);
  if (!value) throw new Error('useSession outside SessionProvider');
  return value;
}

export function useApi(): Api {
  return useSession().api;
}

/**
 * Every face fetched so far, as an object URL, by key. A key is never reused — a
 * new photograph is a new key — so what is held here is never stale, and the same
 * face on the ride card and in «Профиль» is fetched once. Never revoked: there are
 * a handful of them, and a URL revoked under a screen still showing it goes blank.
 */
const faces = new Map<string, Promise<string>>();

/**
 * The face behind [key] as something an `<img src>` takes, or null while there is
 * none: no key, still on its way, or failed — initials stand in for all three.
 */
export function useAvatar(key: string | null | undefined): string | null {
  const api = useApi();
  const [url, setUrl] = useState<{ key: string; url: string } | null>(null);

  useEffect(() => {
    if (!key) return;
    let live = true;
    let face = faces.get(key);
    if (!face) {
      face = getAvatar(api, key).then((blob) => URL.createObjectURL(blob));
      // A failure is forgotten, so the next screen to want this face asks again.
      face.catch(() => faces.delete(key));
      faces.set(key, face);
    }
    face.then((made) => live && setUrl({ key, url: made })).catch(() => {});
    return () => {
      live = false;
    };
  }, [api, key]);

  // Tied to the key it was fetched for, so a photograph just replaced or removed
  // does not linger while the next one loads.
  return url && url.key === key ? url.url : null;
}

/** A car taken and not yet at the pickup: what «Заказ принят» and «Водитель выехал» are about. */
const COMING = new Set(['ACCEPTED', 'EN_ROUTE']);

const LIVE = new Set([
  'SCHEDULED', 'SEARCHING', 'ACCEPTED', 'EN_ROUTE', 'ON_SITE', 'IN_PROGRESS',
]);

export const isLive = (ride: PassengerRideView) => LIVE.has(ride.status);

/**
 * Whether the conversation `tag` names is being read this moment: on screen, in a tab
 * somebody is looking at. A chat left open in a tab behind another one is not.
 */
export function isReading(open: string | null, tag: string): boolean {
  return open === tag && document.visibilityState === 'visible';
}

/**
 * Whether Web Push says it rather than this tab: the browser is subscribed and the
 * tab is hidden, which the server counts as nobody watching (ADR 0027). Said by
 * both, it would arrive twice.
 */
function pushSaysIt(endpoint: string | null): boolean {
  return endpoint !== null && document.visibilityState !== 'visible';
}

/** Whether the driver has written anything not yet read on a ride still going. */
export const hasUnread = (ride: PassengerRideView) => isLive(ride) && ride.unread > 0;
export const isCancelled = (ride: PassengerRideView) =>
  ride.status === 'CANCELLED' || ride.status === 'NO_SHOW' || ride.status === 'EXPIRED';
export const hasDriver = (ride: PassengerRideView) =>
  ['ACCEPTED', 'EN_ROUTE', 'ON_SITE', 'IN_PROGRESS'].includes(ride.status);

/** «Подача» and everything after it: the driver has set off. */
const DEPARTED = new Set(['EN_ROUTE', 'ON_SITE', 'IN_PROGRESS']);

/**
 * A предзаказ nobody has set off for yet: an arrangement rather than a ride, read in
 * «Запланированные». From «Подача» on there is a car coming, a driver to ring and a
 * fare about to start, and it takes the first tab like any other ride — leaving it in
 * a list of things that have not happened yet had a car on its way to somebody while
 * «Сейчас» stayed on offer beside it. Ported from `PassengerRide.awaitsDeparture`.
 */
export const awaitsDeparture = (ride: PassengerRideView) =>
  Boolean(ride.scheduledFor) && !DEPARTED.has(ride.status);

/**
 * The one ride that owns the first tab: searching, or with a car assigned, or just
 * finished and waiting to be paid and rated.
 *
 * Ported from `activeRideProvider`, including the part that matters — a ride that has
 * just ended stays in front of the passenger until they have dealt with it, because
 * there is money to transfer and a driver to rate, and «Готово» is the only thing
 * that says they have. Nothing the driver does closes it: «Дальше на линию» settles
 * their side, and this side of the ride does not move when it does — `PaymentView`
 * no longer carries whether the driver has recorded being paid, because how the
 * money was settled is between the two of them. And a search that found nobody
 * stays too, because silently returning to an empty form would leave somebody
 * wondering whether they had ordered anything at all.
 *
 * A предзаказ joins it when its driver sets off and not before — see
 * {@link awaitsDeparture}.
 */
export function useActiveRide(): PassengerRideView | null {
  const { rides, dismissed } = useSession();
  return activeRide(rides, dismissed);
}

/** {@link useActiveRide}, without the session, so it can be read in a spec. */
export function activeRide(
  rides: readonly PassengerRideView[],
  dismissed: ReadonlySet<string>,
  now = Date.now(),
): PassengerRideView | null {
  for (const ride of rides) {
    if (isLive(ride) && !awaitsDeparture(ride)) return ride;
  }
  const recently = now - FINISHED_RIDE_WINDOW;
  for (const ride of rides) {
    if (dismissed.has(ride.id)) continue;
    if (Date.parse(ride.createdAt) < recently) continue;
    if (ride.status === 'COMPLETED') return ride;
    if (ride.status === 'EXPIRED') return ride;
  }
  return null;
}

/**
 * «Запланированные» — every live предзаказ the first tab is not already showing.
 *
 * Written as "not the one on the first tab" rather than "not set off for yet",
 * because the two part company: the server allows a предзаказ alongside a running
 * «Сейчас» ride, so one can reach «Подача» while the tab is held by the other. Read
 * the simpler way it would then be in neither place. The Flutter app's
 * `preordersProvider` says the same.
 */
export function usePreorders(): PassengerRideView[] {
  const { rides } = useSession();
  return preorders(rides, useActiveRide()?.id ?? null);
}

/** {@link usePreorders}, without the session, so it can be read in a spec. */
export function preorders(
  rides: readonly PassengerRideView[],
  onTheRideTab: string | null,
): PassengerRideView[] {
  return rides
    .filter((r) => r.scheduledFor && isLive(r) && r.id !== onTheRideTab)
    .sort((a, b) => Date.parse(a.scheduledFor!) - Date.parse(b.scheduledFor!));
}

/** «История» — everything that is over. */
export function useHistory(): PassengerRideView[] {
  const { rides } = useSession();
  return rides
    .filter((r) => !isLive(r))
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

/**
 * A clock that ticks once a second, so the fare and the waiting chip move the way a
 * meter does. It stops when the tab is hidden: a backgrounded timer on iOS is
 * throttled to nothing useful anyway, and the value is re-read on return.
 *
 * By the server's clock, since everything it is held against is a moment the
 * server wrote: counted by the computer's own, a clock a few seconds out shows a
 * fare the driver's screen and the receipt do not.
 */
export function useNow(active = true): number {
  const [now, setNow] = useState(() => serverClock.now());
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(serverClock.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}
